"use client";
"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Carousel = exports.useCarouselContext = exports.CarouselContext = void 0;
const react_1 = __importDefault(require("react"));
const Box_1 = require("../../foundations/Box");
exports.CarouselContext = react_1.default.createContext({
    currentIndex: 0,
    goToSlide: () => { },
    goToSlideId: () => { },
    nextSlide: () => { },
    previousSlide: () => { }
});
const useCarouselContext = () => {
    const context = react_1.default.useContext(exports.CarouselContext);
    return context;
};
exports.useCarouselContext = useCarouselContext;
const Carousel = ({ children, peek = 60, disableNavigation = {}, hide, showGradient = true, startAt = 0 }) => {
    const [containerHeight, setContainerHeight] = react_1.default.useState(0);
    const [currentIndex, setCurrentIndex] = react_1.default.useState(startAt);
    const itemRefs = react_1.default.useRef([]);
    const [calculatedY, setCalculatedY] = react_1.default.useState(0);
    const [transitionEnabled, setTransitionEnabled] = react_1.default.useState(false);
    const [isTransitioning, setIsTransitioning] = react_1.default.useState(false);
    const handleTransitionEnd = react_1.default.useCallback((event) => {
        if (event.propertyName === "transform") {
            setIsTransitioning(false);
        }
    }, []);
    const goToSlide = react_1.default.useCallback((index) => {
        setTransitionEnabled(true);
        setCurrentIndex(index);
    }, []);
    const goToSlideId = react_1.default.useCallback((id) => {
        const slidesArray = react_1.default.Children.toArray(children);
        const targetIndex = slidesArray.findIndex((child) => react_1.default.isValidElement(child) && child.props.id === id);
        if (targetIndex === -1 || targetIndex === currentIndex) {
            return;
        }
        const slidesCount = slidesArray.length;
        setIsTransitioning(true);
        // Determine the index to simulate moving only one slide
        let intermediateIndex = currentIndex < targetIndex ? targetIndex - 1 : targetIndex + 1;
        // Ensure the index is within bounds
        if (intermediateIndex < 0) {
            intermediateIndex = 0;
        }
        if (intermediateIndex >= slidesCount) {
            intermediateIndex = slidesCount - 1;
        }
        const intermediateRef = itemRefs.current[intermediateIndex];
        const intermediateOffset = (intermediateRef === null || intermediateRef === void 0 ? void 0 : intermediateRef.offsetTop) - peek;
        // Disable transition to jump instantly
        requestAnimationFrame(() => {
            setTransitionEnabled(false);
        });
        // Set to the position of the slide before the target
        setCalculatedY(intermediateOffset);
        setCurrentIndex(intermediateIndex);
        // Re-enable transition and start the one-slide transition
        requestAnimationFrame(() => {
            setTransitionEnabled(true);
            const targetRef = itemRefs.current[targetIndex];
            const targetOffset = (targetRef === null || targetRef === void 0 ? void 0 : targetRef.offsetTop) - peek;
            setCurrentIndex(targetIndex);
            setCalculatedY(targetOffset);
        });
    }, [children, currentIndex, itemRefs, peek]);
    const nextSlide = react_1.default.useCallback(() => {
        setTransitionEnabled(true);
        setCurrentIndex((prevIndex) => (prevIndex + 1) % react_1.default.Children.count(children));
    }, [children]);
    const previousSlide = react_1.default.useCallback(() => {
        setTransitionEnabled(true);
        setCurrentIndex((prevIndex) => (prevIndex - 1 + react_1.default.Children.count(children)) %
            react_1.default.Children.count(children));
    }, [children]);
    const updateHeight = react_1.default.useCallback(() => {
        if (currentIndex === 0) {
            return setContainerHeight(itemRefs.current[0].clientHeight + peek);
        }
        // Find max height
        const maxHeight = Math.max(...itemRefs.current.map((item) => item.clientHeight + peek * 2));
        setContainerHeight(maxHeight);
    }, [itemRefs, currentIndex, peek]);
    react_1.default.useEffect(() => {
        updateHeight();
    }, [currentIndex, updateHeight]);
    // Calculate and set new Y position when currentIndex changes
    react_1.default.useEffect(() => {
        const currentRef = itemRefs.current[currentIndex];
        const itemOffset = (currentRef === null || currentRef === void 0 ? void 0 : currentRef.offsetTop) - peek;
        setCalculatedY(itemOffset);
    }, [currentIndex, peek]);
    const handleOnClick = (index) => {
        if ((index !== currentIndex &&
            !disableNavigation.next &&
            index > currentIndex) ||
            (!disableNavigation.previous && index < currentIndex)) {
            goToSlide(index);
        }
    };
    const shouldHide = (index) => {
        if (isTransitioning && index !== currentIndex) {
            return true;
        }
        if ((hide === null || hide === void 0 ? void 0 : hide.next) && index > currentIndex) {
            return true;
        }
        else if ((hide === null || hide === void 0 ? void 0 : hide.previous) && index < currentIndex) {
            return true;
        }
        return false;
    };
    const contextValue = {
        currentIndex,
        goToSlide,
        goToSlideId,
        nextSlide,
        previousSlide
    };
    const calculateGradientHeight = react_1.default.useCallback(() => {
        const currentRef = itemRefs.current[currentIndex];
        const currentHeight = currentRef === null || currentRef === void 0 ? void 0 : currentRef.clientHeight;
        if (currentIndex === react_1.default.Children.count(children) - 1) {
            return 0;
        }
        else if (currentIndex === 0) {
            return peek;
        }
        else if (containerHeight - currentHeight >= peek * 2) {
            return containerHeight - currentHeight - peek;
        }
    }, [children, containerHeight, currentIndex, peek]);
    return (react_1.default.createElement(exports.CarouselContext.Provider, { value: contextValue },
        react_1.default.createElement(Box_1.Box, { sx: {
                display: "flex",
                flexDirection: "column",
                height: `${containerHeight}px`,
                overflow: "hidden",
                position: "relative",
                transition: "height 0.1s ease-in-out"
            } },
            showGradient && (react_1.default.createElement(react_1.default.Fragment, null,
                react_1.default.createElement(Box_1.Box, { id: "gradient-top", sx: {
                        position: "absolute",
                        top: 0,
                        left: 0,
                        background: "gradientTop",
                        width: "100%",
                        height: currentIndex !== 0 ? `${peek}px` : 0,
                        zIndex: 4,
                        pointerEvents: "none"
                    } }),
                react_1.default.createElement(Box_1.Box, { id: "gradient-bottom", sx: {
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        background: "gradientBottom",
                        width: "100%",
                        height: `${calculateGradientHeight()}px`,
                        zIndex: 4,
                        pointerEvents: "none",
                        transition: "height 0.1s ease-in-out"
                    } }))),
            react_1.default.createElement(Box_1.Box, { sx: {
                    transition: transitionEnabled ? "easeOutFast" : "none",
                    transform: `translateY(-${calculatedY}px)`
                }, onTransitionEnd: handleTransitionEnd }, react_1.default.Children.map(children, (child, index) => {
                if (react_1.default.isValidElement(child)) {
                    const id = `carousel-item-${index}`;
                    return (react_1.default.createElement(Box_1.Box, { key: child.props.id || id, "data-testid": child.props.id || id, id: child.props.id || id, onClick: () => handleOnClick(index), ref: (el) => {
                            if (el) {
                                itemRefs.current[index] = el;
                            }
                        }, sx: {
                            p: 5,
                            opacity: index === currentIndex ? 1 : 0.5,
                            transition: "easeOutFast"
                        } }, react_1.default.cloneElement(child, {
                        hideContent: child.props.hideContent || shouldHide(index),
                        transitionEnabled
                    })));
                }
            })))));
};
exports.Carousel = Carousel;
//# sourceMappingURL=Carousel.js.map